import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import MGrid, { MGridColumnDefinition } from "components/Shared/MGrid.tsx";
import { defaultHeightStyle } from "styles/sharedStyles";
import { Button, MDialog } from "components/Shared";
import { DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_INSIGHT_NOTIFICATION_VIEW, CUSTOMER_INSIGHT_NOTIFICATION_EDIT } from "typeConstants/rights";
import { CustomerInsightNotificationResponse } from "interfaces/Mueller.Business.Models.ts";
import { getCustomerInsightNotificationsByAccount } from "requests/customerInsights";
import responseHandler from "responseHandler/index.js";
import CustomerInsightNotification from "components/Customer/Insights/CustomerInsightNotification.tsx";

interface CustomerInsightAccountNotificationListProps {
  customerNumber: number;
  gridWrapperStyle?: React.CSSProperties;
  // Redux
  canViewCustomerDetails: boolean;
  canEditCustomerDetails: boolean;
  errorToast: (response: Response) => void;
}

const CustomerInsightAccountNotificationList = (props: CustomerInsightAccountNotificationListProps) => {
  const [selectedNotification, setSelectedNotification] = useState<number>(null);
  const [selectedNotificationRow, setSelectedNotificationRow] = useState<number>(null);
  const [customerInsightNotifications, setCustomerInsightNotifications] = useState<
    CustomerInsightNotificationResponse[]
  >([]);
  const [fetchingCustomerInsightNotifications, setFetchingCustomerInsightNotifications] = useState<boolean>(false);
  const [displayNotificationDialog, setDisplayNotificationDialog] = useState<boolean>(false);
  const mounted = useRef(true);

  useEffect(() => {
    loadCustomerInsightNotifications();
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadCustomerInsightNotifications = () => {
    if (props.customerNumber) {
      setFetchingCustomerInsightNotifications(true);
      getCustomerInsightNotificationsByAccount(
        props.customerNumber,
        (resp) => {
          setCustomerInsightNotifications(resp);
          setFetchingCustomerInsightNotifications(false);
        },
        (resp) => {
          props.errorToast(resp);
          setFetchingCustomerInsightNotifications(false);
        },
        mounted,
      );
    }
  };

  const columns = useMemo(() => {
    const columns: MGridColumnDefinition[] = [
      { key: "userName", name: "Name", dataSource: "name" },
      { key: "userEmail", name: "Email", dataSource: "email" },
      { key: "companyName", name: "Company", dataSource: "majorCompany" },
      {
        key: "companyRollup",
        name: "Major Company Rollup",
        dataSource: "isMajorCompanyRollup",
        dataType: DATA_TYPE_BOOLEAN,
      },
      { key: "accountReport", name: "Account Report", dataSource: "isAccountLevel", dataType: DATA_TYPE_BOOLEAN },
    ];
    return columns;
  }, [props.canEditCustomerDetails]);

  const onCustomerInsightClick = (index) => {
    let selected = customerInsightNotifications[index].id;
    setSelectedNotificationRow(index);
    setSelectedNotification(selected);
    setDisplayNotificationDialog(true);
  };

  const onCloseClick = () => {
    setSelectedNotification(null);
    setSelectedNotificationRow(null);
    setDisplayNotificationDialog(false);
  };

  const getDialogActions = () => {
    let actions = [];
    actions.push(
      <Button primary raised onClick={onCloseClick}>
        Close
      </Button>,
    );
    return actions;
  };

  return (
    <>
      <MGrid
        headerText="Customer Insights"
        columnDefinitions={columns}
        data={customerInsightNotifications}
        isFetchingData={fetchingCustomerInsightNotifications}
        onRowClick={props.canEditCustomerDetails ? onCustomerInsightClick : null}
        rowLastClicked={selectedNotificationRow}
        displayNumOfItemsFound={true}
        cardStyle
        wrapperStyle={props.gridWrapperStyle ?? defaultHeightStyle}
      />
      <MDialog
        visible={displayNotificationDialog}
        id="account-notification-dialog"
        title={""}
        width={600}
        contentActions={getDialogActions()}
      >
        <CustomerInsightNotification
          selectedNotification={selectedNotification}
          refreshParentList={loadCustomerInsightNotifications}
        />
      </MDialog>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  errorToast(response: Response) {
    responseHandler.error(response, dispatch, false);
  },
});

const mapStateToProps = (state) => ({
  canViewCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_VIEW)(state),
  canEditCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_EDIT)(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerInsightAccountNotificationList);
